/**
 * AccountsScreen — multi-account management
 */

import { useState } from 'react'
import { accountManager, formatAccountDisplay } from '@properwallet/wallet-core'
import { useWalletStore } from '../store/wallet.store'
import { useTranslation } from '../hooks/useTranslation'
import BottomNav from '../components/BottomNav'
import AccountAvatar from '../components/AccountAvatar'

export default function AccountsScreen() {
  const { t } = useTranslation()
  const { accounts, activeAccount, setActiveAccount, goBack, init } = useWalletStore()
  const [removing, setRemoving] = useState<string | null>(null)

  const handleRemove = async (name: string) => {
    if (!confirm(t('accounts.confirmRemove'))) return
    setRemoving(name)
    try {
      await accountManager.removeAccount(name)
      await init()
    } finally {
      setRemoving(null)
    }
  }

  return (
    <div className="flex flex-col h-full bg-surface">
      {/* Header */}
      <div className="bg-surface-1 pt-safe px-4 py-3 border-b border-surface-3 flex items-center gap-3 flex-shrink-0">
        <button onClick={goBack} className="text-gray-400 text-sm">
          ← {t('common.back')}
        </button>
        <AccountAvatar avatarUrl={activeAccount?.avatarUrl} size="xs" />
        <h2 className="text-white font-semibold flex-1 text-center">{t('accounts.title')}</h2>
        <div className="w-16" />
      </div>

      <div className="scroll-area flex-1 px-4 py-4 space-y-2">
        {accounts.map(acc => {
          const isActive = acc.name === activeAccount?.name
          return (
            <div
              key={acc.name}
              className={`card flex items-center gap-3 ${
                isActive ? 'border border-brand/40' : ''
              }`}
            >
              <AccountAvatar avatarUrl={acc.avatarUrl} size="md" />

              {/* Info */}
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium text-sm truncate">{formatAccountDisplay(acc)}</p>
                <p className="text-gray-500 text-xs font-mono truncate">{acc.publicKey.slice(0, 20)}...</p>
              </div>

              {/* Active badge */}
              {isActive && (
                <span className="text-xs bg-brand/20 text-brand-light px-2 py-0.5 rounded-full flex-shrink-0">
                  {t('accounts.active')}
                </span>
              )}

              {/* Switch button */}
              {!isActive && (
                <button
                  onClick={() => setActiveAccount(acc.name)}
                  className="text-xs text-gray-400 hover:text-white bg-surface-2 px-2 py-1.5 rounded-lg flex-shrink-0"
                >
                  {t('accounts.switchAccount')}
                </button>
              )}

              {/* Remove button */}
              {accounts.length > 1 && (
                <button
                  onClick={() => handleRemove(acc.name)}
                  disabled={removing === acc.name}
                  className="text-danger/60 hover:text-danger text-sm flex-shrink-0"
                >
                  {removing === acc.name ? '...' : '✕'}
                </button>
              )}
            </div>
          )
        })}

        {/* Import additional account */}
        <button
          onClick={() => {/* Navigate to import screen */}}
          className="btn-secondary w-full flex items-center justify-center gap-2 mt-2"
        >
          <span>+</span>
          <span>{t('accounts.addAccount')}</span>
        </button>
      </div>

      <BottomNav />
    </div>
  )
}
